import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EmptyState, SkeletonLoader } from '@shared/design-system/components';
import {
  getPlatformSettings,
  updatePlatformSetting,
  type AdminPlatformSetting,
} from '../../api/adminClient';

/**
 * Platform-wide settings — the keys in `platform_settings` that apply
 * to every site at once, which is why they live in their own pane
 * rather than under Sites or Commission (both strictly per-site).
 *
 * One card, "AI recovery model", with two controls over the same
 * `['admin','settings']` query:
 *
 *  - The MODEL (`openrouter_model`, free text with a curated datalist).
 *    `resolve_openrouter_model` on the server prefers the DB row, then
 *    the `OPENROUTER_MODEL` env var, then a compiled-in default — so
 *    "Use environment default" is a real operation (delete the row),
 *    not a convenience that types the env value in for you.
 *  - MODEL THINKING (`openrouter_thinking`, off | on, default off),
 *    placed directly under the model box on 2026-09-03 because the two
 *    are one decision, not two settings: an admin switched the model to
 *    `z-ai/glm-5.3-flash` on 2026-09-02 (12:53 UTC) and every analysis
 *    after that ended `deadline_exceeded` with zero content deltas — the
 *    failing runs, read on 2026-09-03, showed the model's thinking, on
 *    by its own default, taking 24.1 s / 38.6 s / 104 s per triage step
 *    against a 90 s run budget with a 30 s reserve. With thinking off
 *    the same model measured 3.2 s per step from the support pod that
 *    day (0 reasoning tokens; 6.6 s with tools attached, tool calls
 *    still returned) against 22.6 s on. Same three-tier resolution on
 *    the server (`resolve_openrouter_thinking`: row →
 *    `OPENROUTER_THINKING` env → "off") and the same PUT contract as
 *    the model, but a closed two-value vocabulary. HOW "off" is asked
 *    for is the server's business (`ai/reasoning_control.py` walks a
 *    per-model ladder, because the honest "do not think" field is an
 *    instant HTTP 400 on GLM and DeepSeek R1 and has to be stepped down
 *    to the least thinking they accept); this pane only says WHETHER.
 *
 * Both controls always show `effective_value` and `source` from the
 * server rather than predicting the outcome here, and each has its own
 * mutation and its own alert so a failed save on one cannot show up
 * under the other's buttons. When the payload has no
 * `openrouter_thinking` row at all — an api-server older than the
 * setting — both thinking buttons are disabled instead of letting the
 * PUT go out: the answer would be a 404 `unknown_setting`, which the
 * alert could only echo, not explain (review finding, 2026-09-03).
 */

const OPENROUTER_MODEL_KEY = 'openrouter_model';
const OPENROUTER_THINKING_KEY = 'openrouter_thinking';

/**
 * Curated TOOL-CALLING models. The list is advisory (free text is
 * allowed — OpenRouter adds models faster than we release consoles),
 * but everything on it supports tool calling, because the recovery
 * assistant is a tool-calling loop: a model without it does not error,
 * it "finds" zero runbook candidates. That failure mode is why this
 * setting is surfaced at all.
 */
const CURATED_MODELS = [
  'anthropic/claude-sonnet-4.5',
  'anthropic/claude-haiku-4.5',
  'anthropic/claude-opus-4.1',
  'openai/gpt-4o',
  'openai/gpt-4o-mini',
  'google/gemini-2.5-pro',
  'google/gemini-2.5-flash',
  // Tool calling works (verified from the support pod 2026-09-03: tool
  // calls returned in 6.6 s with tools attached). It is a REASONING
  // model — needs thinking off, the default — to stay inside the 90 s
  // triage budget: 3.2 s per step with thinking off against 22.6 s on,
  // and 104 s once observed in production (read 2026-09-03).
  'z-ai/glm-5.3-flash',
];

/**
 * The server's `THINKING_CHOICES`, spelled here so the select cannot
 * offer a value the endpoint would refuse. "off" is the recommendation
 * and the server's default; "on" sends nothing about thinking at all —
 * the exact request shape every run had before 2026-09-03 — so the
 * model does whatever it does by default.
 */
const THINKING_DEFAULT = 'off';
const THINKING_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'off', label: 'Off — recommended' },
  { value: 'on', label: 'On — model default' },
];
const THINKING_VALUES = THINKING_OPTIONS.map((o) => o.value);

/** Same LOCAL field pattern as CommissionPane's — the form primitives
 *  in this app are deliberately per-pane, not shared components. This
 *  copy adds `list` so the input can offer a datalist while remaining
 *  free text. */
function TextField({
  label,
  value,
  onChange,
  hint,
  error,
  placeholder,
  list,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  error?: string | null;
  placeholder?: string;
  list?: string;
}) {
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <div style={{ fontSize: 13, marginBottom: 4 }}>{label}</div>
      {hint && (
        <div style={{ fontSize: 12, color: 'var(--nx-muted)', marginBottom: 4 }}>{hint}</div>
      )}
      <input
        value={value}
        list={list}
        placeholder={placeholder}
        aria-label={label}
        aria-invalid={error ? true : undefined}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: '100%', ...(error ? { borderColor: 'var(--nx-error)' } : {}) }}
      />
      {error && (
        <div role="alert" style={{ fontSize: 12, color: 'var(--nx-error)', marginTop: 4 }}>
          {error}
        </div>
      )}
    </label>
  );
}

/** The "set by … on …" suffix both controls append to their stored
 *  override; null when there is no override to attribute. */
function SetBy({ setting }: { setting: AdminPlatformSetting | null }) {
  if (!setting?.value || !setting.updated_by_sub) return null;
  return (
    <span>
      {' '}
      — set by {setting.updated_by_sub}
      {setting.updated_at_ms ? ` on ${new Date(setting.updated_at_ms).toLocaleString()}` : ''}
    </span>
  );
}

export function SettingsPane() {
  const queryClient = useQueryClient();
  const [model, setModel] = useState('');
  const [thinking, setThinking] = useState('');

  const settingsQuery = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => getPlatformSettings(),
  });

  const setting: AdminPlatformSetting | null = useMemo(
    () => (settingsQuery.data?.settings ?? []).find((s) => s.key === OPENROUTER_MODEL_KEY) ?? null,
    [settingsQuery.data],
  );

  const thinkingSetting: AdminPlatformSetting | null = useMemo(
    () =>
      (settingsQuery.data?.settings ?? []).find((s) => s.key === OPENROUTER_THINKING_KEY) ?? null,
    [settingsQuery.data],
  );

  // Prefill from the stored override (not from effective_value: an
  // env-sourced model must not silently become a DB row just because
  // someone opened this pane and pressed Save).
  useEffect(() => {
    if (setting) setModel(setting.value ?? '');
  }, [setting]);

  // The thinking select has no "empty" rendering the way a text input
  // does, so it is shown AT the effective value when there is no
  // override — and its Save stays disabled until the selection differs
  // from that baseline, which is how the same "do not freeze the env
  // value into a row by pressing Save" rule holds here. The baseline is
  // the override when there is one, else what the server says is in
  // effect; a value outside the vocabulary (a hand-edited row) falls
  // back to "off" — the same reading the server gives nonsense — so the
  // select never shows a lie.
  const thinkingBaseline = useMemo(() => {
    const raw = thinkingSetting?.value ?? thinkingSetting?.effective_value ?? '';
    return THINKING_VALUES.includes(raw) ? raw : THINKING_DEFAULT;
  }, [thinkingSetting]);

  useEffect(() => {
    setThinking(thinkingBaseline);
  }, [thinkingBaseline]);

  // One mutation per control: a rejected thinking save must render
  // beside the thinking control, not under the model's Save, and vice
  // versa.
  const save = useMutation({
    mutationFn: (value: string | null) => updatePlatformSetting(OPENROUTER_MODEL_KEY, { value }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
    },
  });

  const saveThinking = useMutation({
    mutationFn: (value: string | null) => updatePlatformSetting(OPENROUTER_THINKING_KEY, { value }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
    },
  });

  if (settingsQuery.isLoading) {
    return <SkeletonLoader shape="table-row" count={3} />;
  }

  if (settingsQuery.isError) {
    return (
      <EmptyState
        title="Could not load platform settings"
        description={
          settingsQuery.error instanceof Error ? settingsQuery.error.message : 'Unknown error'
        }
      />
    );
  }

  const cardStyle = {
    border: '1px solid var(--nx-border)',
    borderRadius: 'var(--nx-r)',
    padding: 16,
    background: 'var(--nx-bg2)',
  } as const;

  const mutedStyle = { fontSize: 12, color: 'var(--nx-muted)' } as const;

  return (
    <div style={{ maxWidth: 760 }}>
      <h2 style={{ marginTop: 0 }}>Platform settings</h2>
      <p style={{ fontSize: 13, color: 'var(--nx-muted)' }}>
        These apply to every site at once. Changes take effect on the next AI call — no restart
        involved, the modules read the setting per call.
      </p>

      <section data-testid="ai-model-card" style={cardStyle}>
        <h3 style={{ marginTop: 0 }}>AI recovery model</h3>

        <div data-testid="ai-model-field" role="group" aria-label="Model">
          <p style={{ fontSize: 13, color: 'var(--nx-muted)' }}>
            The OpenRouter model behind “Analyze with AI” on support issues. Stored override:{' '}
            <code>{setting?.value ?? 'unset'}</code>
            <SetBy setting={setting} />
          </p>

          <TextField
            label="Model"
            value={model}
            onChange={setModel}
            list="openrouter-models"
            placeholder="anthropic/claude-sonnet-4.5"
            hint="Pick from the curated list or type any OpenRouter model id. anthropic/claude-sonnet-4.5 is recommended. A reasoning model needs Model thinking left Off (the default)."
          />
          <datalist id="openrouter-models">
            {CURATED_MODELS.map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>

          <p style={{ ...mutedStyle, marginTop: 0 }}>
            {setting ? `Effective: ${setting.effective_value} (${setting.source}). ` : ''}
            Must support tool calling — a model without it silently returns zero runbook candidates.
          </p>

          {save.isError && (
            <div role="alert" style={{ color: 'var(--nx-error)', fontSize: 13, marginBottom: 8 }}>
              {save.error instanceof Error ? save.error.message : 'Update failed'}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => save.mutate(model.trim())}
              disabled={save.isPending || model.trim() === ''}
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => save.mutate(null)}
              disabled={save.isPending}
              title="Clears the override so OPENROUTER_MODEL from the deployment environment applies again."
            >
              Use environment default
            </button>
            {save.isSuccess && !save.isPending && <span style={mutedStyle}>Saved.</span>}
          </div>
        </div>

        {/* Both controls carry a "Save" and a "Use environment default"
            inside one section, so without grouping a screen reader hears
            "Save, button" twice with nothing to tell them apart (review
            finding, 2026-09-03). Each control is a named group — the
            same `role="group"` + `aria-label` shape RecurrenceEditor
            uses, rather than a <fieldset>, because a <legend> would
            repeat the visible "Model" / "Model thinking" labels the
            input and the select already carry — and the thinking
            buttons get distinct accessible names on top, with their
            visible text unchanged. */}
        <div
          data-testid="ai-thinking-field"
          role="group"
          aria-label="Model thinking"
          style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--nx-border)' }}
        >
          <label style={{ display: 'block', marginBottom: 14 }}>
            <div style={{ fontSize: 13, marginBottom: 4 }}>Model thinking</div>
            {/* Only what was measured is named: GLM 5.3 Flash (22.6 s
                default, 24.1 / 38.6 / 104 s in production) and DeepSeek
                R1 (33 s), all on 2026-09-03. Claude and GPT-4o produced
                0 reasoning tokens either way. */}
            <div style={{ ...mutedStyle, marginBottom: 4 }}>
              Off asks the model not to think before answering — reasoning models such as GLM 5.3
              Flash and DeepSeek R1 otherwise think for 20–100 s per step, and an analysis has 90 s
              in total. GLM 5.3 Flash measured 3 s per step with thinking off against 22 s on;
              Claude and GPT-4o are unaffected either way. A model that cannot stop thinking is
              automatically retried at the least thinking it allows.
            </div>
            <select
              aria-label="Model thinking"
              data-testid="ai-thinking-control"
              value={thinking}
              onChange={(e) => setThinking(e.target.value)}
              style={{ width: '100%' }}
            >
              {THINKING_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          <p style={{ ...mutedStyle, marginTop: 0 }}>
            Stored override: <code>{thinkingSetting?.value ?? 'unset'}</code>
            <SetBy setting={thinkingSetting} />
            {'. '}
            {thinkingSetting
              ? `Effective: ${thinkingSetting.effective_value} (${thinkingSetting.source}).`
              : 'The server did not report this setting — the api-server predates it, so saving is disabled here (the request would be refused).'}
          </p>

          {saveThinking.isError && (
            <div role="alert" style={{ color: 'var(--nx-error)', fontSize: 13, marginBottom: 8 }}>
              {saveThinking.error instanceof Error ? saveThinking.error.message : 'Update failed'}
            </div>
          )}

          {/* `thinkingSetting == null` disables BOTH buttons: an
              api-server that predates the key answers either PUT with
              404 unknown_setting. Before 2026-09-03 only Save was held
              back, and only by accident — the baseline happened to
              equal "off", so selecting On re-enabled it, and the clear
              button was never held back at all. */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              aria-label="Save model thinking"
              onClick={() => saveThinking.mutate(thinking)}
              disabled={
                saveThinking.isPending ||
                thinkingSetting == null ||
                thinking === '' ||
                thinking === thinkingBaseline
              }
            >
              {saveThinking.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              aria-label="Use environment default for model thinking"
              onClick={() => saveThinking.mutate(null)}
              disabled={saveThinking.isPending || thinkingSetting == null}
              title="Clears the override so OPENROUTER_THINKING from the deployment environment (else the compiled-in default, off) applies again."
            >
              Use environment default
            </button>
            {saveThinking.isSuccess && !saveThinking.isPending && (
              <span style={mutedStyle}>Saved.</span>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
